import React from 'react';
import PropTypes from 'prop-types';
import Events from '../../components/common/events';
import SafetyScore from './SafetyScore/SafetyScore';
import NotFoundPage from '../../components/pages/notFoundPage';
import lambdaUtil from '../../components/aws/lambda/lambdaUtil';
import ErrorMessage from '../../components/ErrorMessage';
import Selectors from 'APP_ROOT/data/settings/selectors';
import { loadSettings } from 'APP_ROOT/data/settings/actions';
import { Selectors as tagsSelectors } from '../../data/tags';
import { connect } from 'react-redux';
import ProgressOverlay from '../../components/common/ProgressOverlay';
import { strings } from '../../localization';
import { ACTIVE_MENU_ITEM_IDS, ACTIONS as layoutTemplateModularFullscreenDispatchActions } from '../../data/layoutTemplateModularFullscreen/duck';
import CoachingFilter from './CoachingFilter';
import TagPermissions from '../../components/common/tagsPermissions';


const DRIVERS_CUBEJS_LIMIT = 9000;
const MAX_CUBEJS_RETRIES = 5; // Maximum number of retries for "Continue wait"
const CUBEJS_RETRY_DELAY_MS = 1000; // Delay between retries in milliseconds

export class Coaching extends React.Component {

    constructor(props) {

        super(props);
        this.state = {
            userDataLoaded: false,
            userDataError: undefined,
            knownIdentities: [],
            reportingToken: undefined,
            reportingTokenDate: undefined,
            reportingAPIUrl: undefined,
            dataSet: false,
        };
        this.abortFetchController = new AbortController(); // Create a new controller
    }

    componentDidMount () {

        if (!this.props.ravens) {
            this.props.getUserData(this.onUserDataLoaded);
        } else {
            // This should be called anyway
            // getUserData is now called in the parent component
            // so we always get to this Else block
            this.onUserDataLoaded();

            // load user preferences
            if (!this.props.settingsLoaded) {
                this.props.loadSettings();
            }
        }

        if(this.props.tags && this.props.ravens) {
            TagPermissions.setData(this.props.user, {tags: this.props.tags, ravens: this.props.ravens});
            this.setState({dataSet: true});
        }
    }

    componentWillUnmount() {
        if (this.abortFetchController) {
            this.abortFetchController.abort(); // Abort any ongoing fetch requests
        }
    }

    componentDidUpdate(prevProps) {
        if(!this.state.dataSet && this.props.tags && this.props.ravens) {
            TagPermissions.setData(this.props.user, {tags: this.props.tags, ravens: this.props.ravens});
            this.setState({dataSet: true});
        }
    }

    onUserDataLoaded = () => {

        let currentMenuItemId = ACTIVE_MENU_ITEM_IDS.HISTORY_EVENT_REVIEW;
        
        const currentPage = this.props.match?.params?.parentGroupingType;

        if (currentPage && currentPage === 'score') {
            currentMenuItemId = ACTIVE_MENU_ITEM_IDS.HISTORY_SAFETY_SCORE;
        }

        this.props.setMainNavigationActiveMenuItemId(currentMenuItemId);


        document.dispatchEvent(new CustomEvent(Events.HIDE_LOADING_PAGE));

        if (!this.props.features.facial_recognition) {
            this.setState({
                userDataLoaded: true,
            });
            return;
        }

        this.getAllDriverNames()
            .then(
                (knownIdentities) => {
                    this.setState({
                        userDataLoaded: true,
                        knownIdentities: knownIdentities
                    });
                    return;
                },
                (error) => {
                    return Promise.reject(error);
                }
            )
            .catch((error) => {
                // any error
                console.error(error);

                if (error.name === 'AbortError') return;

                this.setState({
                    userDataLoaded: false,
                    userDataError: error
                });
            });
    }

    refreshReportingTokenPromise = () => {

        const stage = this.props.user.stage;
        if (!stage) {
            console.error("Coaching refreshReportingTokenPromise stage not found");
            return Promise.reject(new Error("Coaching refreshReportingTokenPromise stage not found"));
        }
        // check reportingTokenDate in state, if more than 10 minutes ago (assuming 15 minute expiry), refresh token
        if (this.state.reportingTokenDate) {
            const now = new Date();
            if (now - this.state.reportingTokenDate < 10 * 60 * 1000) {
                return Promise.resolve({
                    status: "SUCCESS",
                    token: this.state.reportingToken,
                    apiUrl: this.state.reportingAPIUrl
                });
            }
        }
        return lambdaUtil.getReportingToken(stage)
        .then(
            (data) => {
                const payload = JSON.parse(data.Payload);
                if (payload.status === "SUCCESS") {
                    const status = payload.status;
                    const token = payload.token;
                    const apiUrl = payload.apiUrl;
                    this.setState({
                        reportingToken: token,
                        reportingTokenDate: new Date(),
                        reportingAPIUrl: apiUrl
                    });
                    return Promise.resolve({
                        status: status,
                        token: token,
                        apiUrl: apiUrl
                    });
                }
                return Promise.reject(new Error("Reporting token request failed"));
            },
            (error) => { return Promise.reject(error); }
        )
        .catch( (error) => {
            console.error("Coaching refreshReportingTokenPromise error occurred", error);
            return Promise.reject(error);
        });
    };

    getAllDriverNames = (currentOffset = 0, accumulatedKnownIdentities = [], retryAttempt = 0) => {

        return this.refreshReportingTokenPromise()
        .then(
            (tokenResponse) => { // token and api endpoint

                if (tokenResponse.status === "SUCCESS") {
                    const token = tokenResponse.token;
                    const apiUrl = tokenResponse.apiUrl;
                    const apiEndpoint = "load";

                    const queryString = "query=" + encodeURIComponent(JSON.stringify({
                        timezone: "America/Toronto",
                        dimensions: [
                            "DimDriver.name",
                            "DimDriver.identityId",
                            "DimDriver.deleted"
                        ],
                        filters: [
                            {
                                member: "DimDriver.deleted",
                                operator: "equals",
                                values: ["0"]
                            },
                            {
                                member: "DimDriver.name",
                                operator: "notEquals",
                                values: [null, ""]
                            }
                        ],
                        limit: DRIVERS_CUBEJS_LIMIT,
                        offset: currentOffset // Use the currentOffset parameter
                    }));

                    return fetch(
                        apiUrl + "/" + apiEndpoint + "?" + queryString,
                        {headers: {
                            'Authorization': token,
                            'Content-Type': 'application/json'
                        },
                        signal: this.abortFetchController.signal
                    })
                    .then( (fetchResponse) => {
                        if (fetchResponse.status >= 400) {
                            return Promise.reject(new Error("Bad response from server. Status: " + fetchResponse.status));
                        }
                        return fetchResponse.json();
                    })
                    .then(
                        (queryResponse) => {

                            if (queryResponse.error) {
                                if (queryResponse.error === 'Continue wait') {
                                    if (retryAttempt < MAX_CUBEJS_RETRIES) {
                                        return new Promise((resolve, reject) => {
                                            const delay = CUBEJS_RETRY_DELAY_MS * Math.pow(2, retryAttempt);
                                            setTimeout(() => {
                                                this.getAllDriverNames(currentOffset, accumulatedKnownIdentities, retryAttempt + 1)
                                                    .then(resolve)
                                                    .catch(reject);
                                            }, delay);
                                        });
                                    } else {
                                        return Promise.reject(new Error(`Failed to fetch driver names after ${MAX_CUBEJS_RETRIES} retries.`));
                                    }
                                }
                                else{
                                    console.error("Coaching getAllDriverNames: server returned an error:", queryResponse.error);
                                    return Promise.reject(new Error("drivers query failed: " + queryResponse.error));
                                }                            
                            }

                            const resultsArray = queryResponse.data ? queryResponse.data : [];
                            let newIdentitiesFromThisCall = [];
                            resultsArray.forEach((basicDriverResult) => {
                                if (basicDriverResult["DimDriver.name"] && basicDriverResult["DimDriver.identityId"]) {
                                    newIdentitiesFromThisCall.push({
                                        identityId: String(basicDriverResult["DimDriver.identityId"]),
                                        name: basicDriverResult["DimDriver.name"]
                                    });
                                }
                            });

                            const allSoFarIdentities = accumulatedKnownIdentities.concat(newIdentitiesFromThisCall);
                            if (resultsArray.length >=  DRIVERS_CUBEJS_LIMIT) {
                                // if we received the limit number of results, call again with updated offset and accumulated data
                                const nextOffset = currentOffset +  1 * DRIVERS_CUBEJS_LIMIT; // increment by the limit
                                return this.getAllDriverNames(nextOffset, allSoFarIdentities); // retryAttempt will be default 0 for new page
                            } else {
                                // No more data to fetch, so we are done.
                                return Promise.resolve(allSoFarIdentities);
                            }
                        }
                        // Error in fetch().json() or subsequent processing will be caught by the outer .catch
                    );
                }
                
                // Token request was not successful
                return Promise.reject(new Error("Reporting token request failed with status: " + tokenResponse.status));
            }
            // Error in refreshReportingTokenPromise() itself will be caught by the outer .catch
        )
        .catch((error) => {
            if (error.name === 'AbortError') {
                console.log('Coaching getAllDriverNames: Fetch operation was aborted.');
            } else {
                // This catch handles errors from refreshReportingTokenPromise, tokenResponse status failure, fetch, or json parsing.
                console.error("Coaching getAllDriverNames error occurred", error);
            }
            return Promise.reject(error); // Propagate the error
        });
    }

    getVehicleNamesWithTags = () => {
        if (!this.props.ravens ||
            !this.props.ravens.length) {
            return;
        }
        let vehicles = [];
        this.props.ravens.forEach((raven) => {
            const vehicleName = raven && raven.vehicle ? raven.vehicle.name : undefined;
            const vehicleTags = raven && raven.tags ? raven.tags.map(t => t.tagId) : [];
            if (!vehicleName) {
                console.warn("Coaching Review unnamed vehicle found", raven);
                return;
            }
            vehicles.push({name: vehicleName, tags: vehicleTags});
        });
        return vehicles;
    }

    onOptionsClick = () => {
        this.props.setMainModuleGroupNavigation(false); // isCollapsed false
    }

    render () {

        if (this.state.userDataError) {
            return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100vh" }}><ErrorMessage error={(this.state.userDataError)} /></div>;
        }

        if (!this.props.features ||
            !this.state.userDataLoaded ||
            !this.props.user ||
            !this.state.dataSet) {
            return <div className="coaching-preloader"><ProgressOverlay /></div>
        }


        const appNamePrefix = this.props.features && this.props.features.app_name_prefix ? this.props.features.app_name_prefix + " | " : "";
        switch (this.props.match.params.parentGroupingType) {
            case "events":
                document.title = appNamePrefix + strings.app_name_suffix + " - " + strings.event_review;
                return (
                    // to be upgraded to use CoachingFilter when merging with Event Review improvements
                    <div className='coaching-page'>
                        <CoachingFilter
                            {...this.props}
                            vehicles={this.getVehicleNamesWithTags()}
                            knownIdentities={this.state.knownIdentities}// name and id et al: [{"identityId":"","name":"","profileFace":{"faceId":"","secureImageUrl":"","detectedTime":"2019-07-10T00:22:17"}}]
                        />
                    </div>
                );
            case "score":
                document.title = appNamePrefix + strings.app_name_suffix + " - " + strings.safety_score;
                return (
                    <SafetyScore

                        // ModuleGroupNavigiation state
                        setMainModuleGroupNavigation={this.props.setMainModuleGroupNavigation}
                        onOptionsClick={this.onOptionsClick}

                        // credentials
                        refreshReportingTokenPromise={this.refreshReportingTokenPromise}

                        // Account and the current user
                        accountType={this.props.accountDetails ? this.props.accountDetails.accountType : undefined} // "FLEET" or "CONSUMER"(?)
                        user={this.props.user} // used by Query Renderer for stage and possibly other user data

                        // Drivers
                        facialRecognitionSupported={this.props.features ? this.props.features.facial_recognition : undefined}
                        facialRecognitionEnabled={this.props.facialRecognitionEnabled}

                        knownIdentities={this.state.knownIdentities} // name and id et al: [{"identityId":"","name":"","profileFace":{"faceId":"","secureImageUrl":"","detectedTime":"2019-07-10T00:22:17"}}]

                        // Vehicles' Ravens
                        ravens={this.props.ravens}

                        // User Preferences
                        unitOfMeasure={this.props.unitOfMeasure}

                    />
                );
            default:
                return <NotFoundPage {...this.props} />;
        }
    }

}

Coaching.propTypes = {
    // From parent/ownProps
    user: PropTypes.object.isRequired,
    features: PropTypes.object.isRequired,
    ravens: PropTypes.array.isRequired,
    accountDetails: PropTypes.object,
    getUserData: PropTypes.func.isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            parentGroupingType: PropTypes.string
        })
    }).isRequired,
    
    // From mapStateToProps
    tags: PropTypes.array,
    tagsLoaded: PropTypes.bool,
    tagsError: PropTypes.any,
    tagsLoading: PropTypes.bool,
    settingsLoaded: PropTypes.bool,
    unitOfMeasure: PropTypes.string,
    settingsError: PropTypes.any,
    facialRecognitionEnabled: PropTypes.bool,
    
    // From mapDispatchToProps
    loadSettings: PropTypes.func.isRequired,
    setMainModuleGroupNavigation: PropTypes.func.isRequired,
    setMainNavigationActiveMenuItemId: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {

    return {
        tags: tagsSelectors.tags(state),
        tagsLoaded: tagsSelectors.loaded(state),
        tagsError: tagsSelectors.error(state),
        tagsLoading: tagsSelectors.loading(state),
        settingsLoaded: Selectors.settingsLoaded(state),
        unitOfMeasure: Selectors.unitOfMeasure(state),
        settingsError: Selectors.error(state),
        facialRecognitionEnabled: ownProps.features?.facial_recognition && ownProps.features?.facial_recognition_enabled,
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        loadSettings: () => dispatch(loadSettings(ownProps.user.stage)),
        setMainModuleGroupNavigation: (isCollapsed) => dispatch(layoutTemplateModularFullscreenDispatchActions.setMainModuleGroupNavigation(isCollapsed)),
        setMainNavigationActiveMenuItemId: (activeMenuItemId) => {
            dispatch(layoutTemplateModularFullscreenDispatchActions.setMainNavigationActiveMenuItemId(activeMenuItemId));
            return;
        },
    }
};

export {Coaching as UnconnectedCoaching}; // Added for testing
export default connect(mapStateToProps, mapDispatchToProps)(Coaching);